import React from "react"
import { flexRender, Table as TableInterFace } from "@tanstack/react-table"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import ViewApplicationModal from "../ViewApplicationModal/ViewApplicationModal"
import { useNavigate, useSearchParams } from "react-router-dom"
import { APP_ROUTES } from "@/common/urls"

interface DataTableProps<TData> {
    table: TableInterFace<TData>
    isFetching: boolean
    userRole: string
    onSelectionChange?: (selectedRows: string[]) => void
}

export function DataTable<TData>({
    table,
    userRole,
    onSelectionChange,
}: DataTableProps<TData>) {
    const [searchParams, setSearchParams] = useSearchParams()
    const navFunc = useNavigate()
    const [selectedRows, setSelectedRows] = React.useState<string[]>([])

    // Get values from search params
    const applicationId = searchParams.get("applicationId") || ""

    // Track filter changes separately from modal state
    const filterParams = React.useMemo(() => {
        const params = new URLSearchParams(searchParams)
        params.delete("viewApplication")
        params.delete("applicationId")
        return params.toString()
    }, [searchParams])

    // Reset selection state only when filters change
    React.useEffect(() => {
        setSelectedRows([])
    }, [filterParams])

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allRowIds = table
                .getRowModel()
                .rows.map((row) => (row.original as any).applicationId)
            setSelectedRows(allRowIds)
            onSelectionChange?.(allRowIds)
        } else {
            setSelectedRows([])
            onSelectionChange?.([])
        }
    }

    const handleSelectRow = (checked: boolean, rowId: string) => {
        const updatedSelection = checked
            ? [...selectedRows, rowId]
            : selectedRows.filter((id) => id !== rowId)
        setSelectedRows(updatedSelection)
        onSelectionChange?.(updatedSelection)
    }

    function closeAdminModal() {
        // Clear the search params when closing
        searchParams.delete("applicationId")
        setSearchParams(searchParams)
    }

    function navigateToApplication(id: string) {
        navFunc(APP_ROUTES.ROOT.APPLICATION.replace(":id", id))
    }

    function handleRowClick(id: string, event: React.MouseEvent) {
        // Prevent row click when clicking checkbox
        if ((event.target as HTMLElement).closest(".checkbox-cell")) {
            return
        }

        if (userRole !== "applicant") {
            // Preserve existing params and add modal params
            const newParams = new URLSearchParams(searchParams)
            newParams.set("applicationId", id)
            setSearchParams(newParams)
        } else {
            navigateToApplication(id)
        }
    }

    return (
        <div className="rounded-md border relative">
            <Table>
                <TableHeader>
                    <TableRow>
                        {userRole === "admin" && (
                            <TableHead className="w-[50px]">
                                <Checkbox
                                    checked={
                                        table.getRowModel().rows.length > 0 &&
                                        selectedRows.length ===
                                            table.getRowModel().rows.length
                                    }
                                    onCheckedChange={(checked) =>
                                        handleSelectAll(checked as boolean)
                                    }
                                />
                            </TableHead>
                        )}
                        {table.getHeaderGroups().map((headerGroup) => (
                            <React.Fragment key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef
                                                      .header,
                                                  header.getContext()
                                              )}
                                    </TableHead>
                                ))}
                            </React.Fragment>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                            <TableRow
                                key={row.id}
                                data-state={row.getIsSelected() && "selected"}
                                onClick={(e) =>
                                    handleRowClick(
                                        (row.original as any).applicationId,
                                        e
                                    )
                                }
                                className="cursor-pointer"
                            >
                                {userRole === "admin" && (
                                    <TableCell className="checkbox-cell">
                                        <Checkbox
                                            checked={selectedRows.includes(
                                                (row.original as any)
                                                    .applicationId
                                            )}
                                            onCheckedChange={(checked) =>
                                                handleSelectRow(
                                                    checked as boolean,
                                                    (row.original as any)
                                                        .applicationId
                                                )
                                            }
                                        />
                                    </TableCell>
                                )}
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell key={cell.id}>
                                        {flexRender(
                                            cell.column.columnDef.cell,
                                            cell.getContext()
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell
                                colSpan={table.getAllColumns().length}
                                className={
                                    "inline-block text-gray-700 sticky left-0 h-18 text-start self-center text-nowrap"
                                }
                            >
                                No results.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            <ViewApplicationModal
                open={!!applicationId}
                onOpenChange={closeAdminModal}
                applicationId={applicationId}
            />
        </div>
    )
}
